import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';
import type {
  HistoryMultiSearchBody,
  HistorySearchParams,
  HistorySearchParent,
  HistorySearchResponse,
} from './types';

/**
 * :
 * GET /history/v1/search?parentType=&parentId=&entityType=&page=&pageSize=&sortAscending=
 * The boolean param `sortAscending` is sent as `'true'`/`'false'` (string).
 */
function buildHistoryQuery(params: HistorySearchParams): string {
  const parts: string[] = [];
  const add = (key: string, value: unknown): void => {
    if (value === undefined || value === null || value === '') return;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  };
  add('parentType', params.parentType);
  add('parentId', params.parentId);
  add('entityType', params.entityType);
  add('page', params.page);
  add('pageSize', params.pageSize);
  if (params.sortAscending !== undefined) {
    add('sortAscending', params.sortAscending === 'asc');
  }
  add('movedToPermanentStorage', params.movedToPermanentStorage);
  return parts.join('&');
}

/**
 * The GET's `Order:<id>` tag for each order parent, so the order page's live refresh
 * also reaches order-anchored searches. The body's parent type is lowercase.
 */
function orderParentTags(parents: HistorySearchParent[]): { type: 'History'; id: string }[] {
  const orderIds = new Set(
    parents.flatMap(({ parentType, parentId }) =>
      parentType?.toLowerCase() === 'order' && parentId ? [parentId] : [],
    ),
  );
  return [...orderIds].map((orderId) => ({ type: 'History', id: `Order:${orderId}` }));
}

export const historyApi = createApi({
  reducerPath: 'historyApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['History'],
  endpoints: (builder) => ({
    getHistory: builder.query<HistorySearchResponse, HistorySearchParams>({
      query: (params) => ({
        url: `${services.history}/v1/search?${buildHistoryQuery(params)}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, params) => [
        { type: 'History', id: `${params.parentType}:${params.parentId}` },
      ],
    }),

    /**
     * POST /history/v1/search — multi-parent/-entity search used by the
     * shipment History Logs tab (the legacy `searchHistory` saga). The GET
     * form above takes a single parent; this one accepts arrays so the tab
     * can scope to a shipment AND its packages in one call. Body shape from
     * the legacy `historyServices.searchHistory(body)`.
     */
    searchHistory: builder.query<HistorySearchResponse, HistoryMultiSearchBody>({
      query: (body) => ({
        url: `${services.history}/v1/search`,
        method: 'POST',
        body,
      }),
      providesTags: (_result, _error, body) => [
        { type: 'History', id: 'MULTI' },
        ...orderParentTags(body.parents),
      ],
    }),
  }),
});

export const { useGetHistoryQuery, useSearchHistoryQuery } = historyApi;
